import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, ChevronLeft } from "lucide-react";
import { toast } from "sonner";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString([], { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }) : "");

const who = (job) => job.started_by_name || (job.started_by === "cron" ? "Nightly sync" : "Manual");
const errText = (e) => (/429/.test(e || "") ? "Jackrabbit busy, class skipped (no changes)" : e);

const GROUPS = [
  { key: "added", label: "Added", color: "text-emerald-700 bg-emerald-50" },
  { key: "restored", label: "Restored", color: "text-sky-700 bg-sky-50" },
  { key: "dropped", label: "Dropped", color: "text-[#D9232E] bg-red-50" },
  { key: "missing_in_app", label: "Skipped (no phone)", color: "text-amber-700 bg-amber-50" },
  { key: "review", label: "Needs review", color: "text-violet-700 bg-violet-50" },
  { key: "phones_updated", label: "Numbers updated", color: "text-teal-700 bg-teal-50" },
  { key: "family_fixes", label: "Family fixes", color: "text-indigo-700 bg-indigo-50" },
  { key: "errors", label: "Errors", color: "text-slate-700 bg-slate-100" },
];

const countFor = (job, key) => ({
  added: job.added_count, restored: job.restored_count, dropped: job.dropped_count,
  missing_in_app: job.missing_in_app_count, errors: job.errored, review: job.review_count, phones_updated: job.phones_updated_count, family_fixes: job.family_fixes_count,
}[key] || 0);

function JobRow({ job, onOpen }) {
  return (
    <button onClick={() => onOpen(job.id)} data-testid={`sync-history-row-${job.id}`}
      className="w-full text-left px-4 py-3 border-b border-slate-100 hover:bg-slate-50 transition-colors">
      <div className="flex items-center justify-between gap-2">
        <div className="text-sm font-semibold text-slate-900">{fmt(job.created_at)}</div>
        <div className="text-[11px] text-slate-500">{who(job)} · {job.status}</div>
      </div>
      <div className="flex flex-wrap gap-1.5 mt-1.5">
        {GROUPS.map((g) => (
          <span key={g.key} className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${countFor(job, g.key) ? g.color : "text-slate-400 bg-slate-50"}`}>
            {g.label} {countFor(job, g.key)}
          </span>
        ))}
        {!!job.merged_count && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded text-slate-600 bg-slate-100">Merged {job.merged_count}</span>}
        {!!job.cleaned_count && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded text-slate-600 bg-slate-100">Leftovers removed {job.cleaned_count}</span>}
      </div>
    </button>
  );
}

function JobDetail({ job, onBack }) {
  return (
    <div data-testid="sync-history-detail">
      <button onClick={onBack} className="flex items-center text-xs text-slate-500 hover:text-slate-900 mb-3" data-testid="sync-history-back">
        <ChevronLeft className="w-4 h-4" /> All syncs
      </button>
      <div className="text-sm font-semibold text-slate-900 mb-3">{fmt(job.created_at)} · {who(job)}</div>
      <div className="space-y-4 max-h-[60vh] overflow-y-auto thin-scroll pr-1">
        {GROUPS.map((g) => {
          const rows = job[g.key] || [];
          return (
            <div key={g.key} data-testid={`sync-detail-${g.key}`}>
              <div className={`inline-block text-[11px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${g.color}`}>{g.label} · {rows.length}</div>
              {rows.length === 0 ? <div className="text-xs text-slate-400 italic mt-1">None</div> : (
                <ul className="mt-1.5 divide-y divide-slate-100 border border-slate-100 rounded-md">
                  {rows.map((r, i) => (
                    <li key={i} className="px-3 py-1.5 text-xs flex justify-between gap-3 min-w-0">
                      <span className="font-medium text-slate-800 break-words min-w-0">{r.student_name || r.class}</span>
                      <span className="text-slate-500 text-right break-words min-w-0">{r.class_name || errText(r.error)}{r.reason ? ` · ${r.reason}` : ""}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export const SyncHistoryDialog = ({ open, onOpenChange }) => {
  const [jobs, setJobs] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loadingId, setLoadingId] = useState(null);

  useEffect(() => {
    if (!open) { return; }
    setDetail(null);
    api.get("/jackrabbit/reconcile-rosters").then(({ data }) => setJobs(data)).catch((e) => toast.error(formatApiError(e)));
  }, [open]);

  const openJob = async (id) => {
    setLoadingId(id);
    try { const { data } = await api.get(`/jackrabbit/reconcile-rosters/${id}`); setDetail(data); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setLoadingId(null); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl w-[calc(100vw-1rem)] sm:w-full overflow-hidden" data-testid="sync-history-dialog">
        <DialogHeader><DialogTitle>Jackrabbit Sync History</DialogTitle></DialogHeader>
        {detail ? <JobDetail job={detail} onBack={() => setDetail(null)} /> : (
          <div className="max-h-[65vh] overflow-y-auto thin-scroll -mx-6 border-t border-slate-100" data-testid="sync-history-list">
            {jobs === null && <div className="p-6 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>}
            {jobs && jobs.length === 0 && <div className="p-6 text-sm text-slate-400 text-center">No syncs yet</div>}
            {jobs && jobs.map((j) => (
              <div key={j.id} className={loadingId === j.id ? "opacity-50" : ""}><JobRow job={j} onOpen={openJob} /></div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
